import { useLocation } from '@reach/router'
import { useEffect, useMemo } from 'react'
import * as Sentry from '@sentry/gatsby'
import { dispatchAmplitudeEvent } from 'src/utils/amplitude'
import { getStandardObject } from 'src/utils/amplitude/getStandardObject'
import type { PageTypeResult } from 'src/hooks/usePageType'

const DY_BUTTON = '.sm_bubble_container'
const DY_PRODUCT_IMAGE = '.dy-muse-message-gallery-item-image'
const DY_SEARCH_INPUT = '.dy-muse-message-wrapper'
// basic-layout welcome screen input has no .dy-muse-message-wrapper ancestor
const DY_MUSE_INPUT_CONTAINER = `${DY_SEARCH_INPUT}, .dy-muse-message-input`
const DY_SUGGESTION_ITEM = '.dy-muse-grid-item.suggestion'
const DY_MUSE_WIDGET_CONTAINER = '#dy-muse-widget-container'
const DY_MUSE_CHAT_CTA = '#dy-chat-cta'
const DY_MUSE_MESSAGE_INPUT_FIELD = '.dy-muse-message-input-field'
const DY_MUSE_MESSAGE_INPUT_BTN = '.dy-muse-message-input-btn'
const DY_MUSE_MESSAGE_INPUT_BTN_TAG = 'button'
const DY_MUSE_SEARCH_BY_IMAGE_BUTTON = 'dy-muse-search-by-image-btn-'
const DY_MUSE_INPUT_SELECTOR =
  'input.dy-muse-message-input-field, input[type="text"]'

const DY_MUSE_TEXTAREA_SELECTOR =
  'textarea.dy-muse-message-input-field, textarea'

const getSiteSection = (pageData: PageTypeResult) =>
  pageData?.currentPageType || 'unknown'

const getShoppingMuseTerm = (target: Element) => {
  const messageWrapper = target.closest(DY_MUSE_INPUT_CONTAINER)
  const inputFromWrapper = messageWrapper?.querySelector<HTMLInputElement>(
    DY_MUSE_INPUT_SELECTOR
  )

  if (inputFromWrapper?.value?.trim()) {
    return inputFromWrapper.value.trim()
  }

  const textAreaFromWrapper =
    messageWrapper?.querySelector<HTMLTextAreaElement>(
      DY_MUSE_TEXTAREA_SELECTOR
    )

  if (textAreaFromWrapper?.value?.trim()) {
    return textAreaFromWrapper.value.trim()
  }

  console.error('[MuseSearch] Term not found in available inputs')

  return ''
}

const sendSentryError = (err: Error, pageData: PageTypeResult) => {
  if (typeof Sentry === 'undefined') {
    console.error('Sentry is not defined')

    return
  }

  Sentry.captureException(err, {
    tags: {
      page: pageData.currentPageType,
      ssr: 'false',
    },
    extra: {
      message: err?.message || 'Unknown error',
      stack: err?.stack ?? 'No stack trace',
    },
  })
}

// aria-label text is locale/version dependent, so identify the button by class only
const isMuseSendMessageButton = (button: Element) => {
  const className = button.className || ''
  const isSearchByImageButton = className.includes(
    DY_MUSE_SEARCH_BY_IMAGE_BUTTON
  )

  const isMuseInputButtonClass = className.includes(
    DY_MUSE_MESSAGE_INPUT_BTN.replace('.', '')
  )

  return isMuseInputButtonClass && !isSearchByImageButton
}

const getMuseSendButton = (target: Element) => {
  const container = target.closest('.dy-muse-message-input')

  if (!container) {
    return null
  }

  const button = target.closest(DY_MUSE_MESSAGE_INPUT_BTN_TAG)

  if (!button || !container.contains(button)) {
    return null
  }

  return isMuseSendMessageButton(button) ? button : null
}

export const useDynamicYieldEvents = (pageData: PageTypeResult) => {
  const { pathname } = useLocation()
  const standardObject = useMemo(() => getStandardObject(pageData), [pageData])

  useEffect(() => {
    let buttonElement: Element | null = null
    // click may never fire if the widget mutates the button right after mousedown, so
    // dispatch is triggered from mousedown instead; this flag avoids a duplicate dispatch
    // if click does end up firing for the same interaction
    let museSearchDispatchedOnMouseDown = false

    const dispatchShoppingMuseSearch = (shoppingMuseTerm: string) => {
      dispatchAmplitudeEvent({
        eventName: 'Shopping Muse Search',
        eventData: {
          ...standardObject,
          'shopping muse term': shoppingMuseTerm,
        },
      })
    }

    const handleDocumentClick = (event: MouseEvent) => {
      try {
        const { target } = event

        if (!(target instanceof Element)) {
          return
        }

        const isMuseClicked =
          target?.closest(DY_MUSE_WIDGET_CONTAINER) ??
          target?.closest(DY_MUSE_CHAT_CTA)

        const museSendButton = getMuseSendButton(target)
        const isMuseSearchButtonClicked = !!museSendButton

        const isProductClicked = target?.closest(DY_PRODUCT_IMAGE)
        const isInputClicked =
          target?.closest(DY_SEARCH_INPUT) ??
          target?.closest(DY_SUGGESTION_ITEM) ??
          target?.closest(DY_PRODUCT_IMAGE)

        if (isMuseClicked) {
          dispatchAmplitudeEvent({
            eventName: 'Shopping Muse Clicked',
            eventData: {
              ...standardObject,
              'site section': getSiteSection(pageData),
            },
          })
        }

        if (isMuseSearchButtonClicked && !museSearchDispatchedOnMouseDown) {
          const shoppingMuseTerm = getShoppingMuseTerm(target)

          if (shoppingMuseTerm) {
            dispatchShoppingMuseSearch(shoppingMuseTerm)
          } else {
            console.warn(
              '[MuseSearch] Skip dispatch on click because term is empty'
            )
          }
        }

        museSearchDispatchedOnMouseDown = false

        if (isProductClicked) {
          const itemContainer = isProductClicked.closest(
            '.dy-muse-message-gallery-item'
          )

          if (!itemContainer) return

          dispatchAmplitudeEvent({
            eventName: 'Product Selected',
            eventData: {
              ...standardObject,
              'product details': {
                'product id':
                  itemContainer.getAttribute('data-dy-product-id') ?? '',
                name:
                  itemContainer.querySelector(
                    '.dy-muse-message-gallery-item-name'
                  )?.textContent ??
                  isProductClicked.getAttribute('alt') ??
                  '',
                'display price':
                  itemContainer.querySelector(
                    '.dy-muse-message-gallery-item-price'
                  )?.textContent ?? '',
                'sku code':
                  itemContainer.getAttribute('data-dy-product-sku') ?? '',
                'shopping tool': 'Recommendation',
              },
              'product list type': 'shopping muse',
              'number of products':
                itemContainer.parentElement?.children.length ?? 0,
              'product position':
                Array.from(itemContainer.parentElement?.children ?? []).indexOf(
                  itemContainer
                ) + 1,
            },
          })
        }

        if (isInputClicked) {
          dispatchAmplitudeEvent({
            eventName: 'Click Input',
            eventData: { ...standardObject, 'click name': 'shopping muse' },
          })
        }
      } catch (error) {
        console.error('Error in handleDocumentClick:', error)
        sendSentryError(error as Error, pageData)
      }
    }

    const handleButtonClick = () => {
      dispatchAmplitudeEvent({
        eventName: 'Click Button',
        eventData: { ...standardObject, 'click name': 'shopping muse' },
      })
    }

    const handleDocumentMouseDown = (event: MouseEvent) => {
      try {
        const { target } = event

        if (!(target instanceof Element)) {
          return
        }

        if (!getMuseSendButton(target)) {
          return
        }

        const shoppingMuseTerm = getShoppingMuseTerm(target)

        if (shoppingMuseTerm) {
          museSearchDispatchedOnMouseDown = true
          dispatchShoppingMuseSearch(shoppingMuseTerm)
        }
      } catch (error) {
        console.error('Error in handleDocumentMouseDown:', error)
        sendSentryError(error as Error, pageData)
      }
    }

    const handleMuseSearchOnEnter = (event: KeyboardEvent) => {
      try {
        const { target } = event

        if (!(target instanceof Element)) {
          return
        }

        if (!target?.closest(DY_MUSE_MESSAGE_INPUT_FIELD)) {
          return
        }

        if (event.key !== 'Enter') {
          return
        }

        const inputLike = target as HTMLInputElement | HTMLTextAreaElement
        const shoppingMuseTerm = inputLike.value?.trim()

        if (!shoppingMuseTerm) {
          console.warn(
            '[MuseSearch] Skip dispatch on Enter because term is empty'
          )

          return
        }

        dispatchShoppingMuseSearch(shoppingMuseTerm)
      } catch (error) {
        console.error('Error in handleMuseSearchOnEnter:', error)
        sendSentryError(error as Error, pageData)
      }
    }

    const setupListener = () => {
      try {
        buttonElement = document.querySelector(DY_BUTTON)

        if (buttonElement) {
          buttonElement.addEventListener('click', handleButtonClick)
        }
      } catch (error) {
        console.error('Error in setupListener:', error)
        sendSentryError(error as Error, pageData)
      }
    }

    const domObserver = new MutationObserver((_, self) => {
      try {
        const targetElement = document.querySelector(DY_BUTTON)

        if (targetElement) {
          setupListener()

          self.disconnect()
        }
      } catch (error) {
        console.error('Error in domObserver:', error)
        sendSentryError(error as Error, pageData)
      }
    })

    domObserver.observe(document.body, { childList: true, subtree: true })
    document.addEventListener('click', handleDocumentClick, true)
    document.addEventListener('keydown', handleMuseSearchOnEnter, true)
    document.addEventListener('mousedown', handleDocumentMouseDown, true)

    return () => {
      try {
        domObserver.disconnect()
        document.removeEventListener('click', handleDocumentClick, true)
        document.removeEventListener('keydown', handleMuseSearchOnEnter, true)
        document.removeEventListener('mousedown', handleDocumentMouseDown, true)
        buttonElement?.removeEventListener('click', handleButtonClick)
      } catch (error) {
        console.error('Error in useEffect cleanup:', error)
        sendSentryError(error as Error, pageData)
      }
    }
  }, [pageData, standardObject])

  useEffect(() => {
    let attempts = 0
    const maxAttempts = 3
    let timeoutId: ReturnType<typeof setTimeout> | null = null

    const tryDispatchEvent = () => {
      try {
        const dyButton = document.querySelector(DY_BUTTON)

        if (!dyButton) {
          throw new Error('DY Button not found')
        }

        dispatchAmplitudeEvent({
          eventName: 'Modal Viewed',
          eventData: {
            ...standardObject,
            modal_nome: 'shopping muse',
          },
        })
      } catch (error) {
        if (attempts < maxAttempts) {
          attempts++
          timeoutId = setTimeout(tryDispatchEvent, 2000)
        }
      }
    }

    tryDispatchEvent()

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [pathname, standardObject])
}
